import type React from 'react';
import {
	type UseDefferedLoadingOptions,
	useDefferedLoading,
} from './useDefferedLoading';

export interface LazyUiProps extends UseDefferedLoadingOptions {
	/**
	 * Whether the operation is currently loading
	 */
	loading: boolean;

	/**
	 * The UI to show while loading (after delay)
	 */
	fallback?: React.ReactNode;

	/**
	 * The main content to show when not loading
	 */
	children?: React.ReactNode;
}

/**
 * Component that delays showing a fallback UI until after a delay,
 * and keeps it visible for a minimum duration to avoid flicker.
 *
 * @example
 * ```tsx
 * <LazyFallback
 *   isLoading={isLoading}
 *   fallback={<Spinner />}
 *   delay={250}
 *   minDuration={200}
 * >
 *   <Content />
 * </LazyFallback>
 * ```
 */
export function LazyUi({
	loading,
	fallback,
	children,
	delay,
	minDuration,
}: LazyUiProps) {
	const showFallback = useDefferedLoading(loading, { delay, minDuration });

	if (showFallback) {
		return fallback;
	}

	if (!loading) {
		return children;
	}

	// Return null during the delay period before showing fallback
	return null;
}
